import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Loader2, Image as ImageIcon, Film, Upload, Columns2, Sparkles, Trash2, ChevronLeft, ChevronRight, Send, CalendarClock, Heart, MessageCircle, ThumbsUp, Repeat2, AlertTriangle, Check } from 'lucide-react';
import { toast } from 'react-toastify';
import { redes, errorMsg, material, ultimoTimelapse, subir } from './redes.api';
import { RED, PLANTILLAS, HASHTAGS, TIPOS, completar, tipoBackend, hoyAR, sumarDias, isoAR, partesAR, cuando } from './util';

const input = 'block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500';

function Miniatura({ m, className = '' }) {
  if (m.subiendo) return <div className={`bg-gray-100 flex items-center justify-center ${className}`}><Loader2 size={18} className="animate-spin text-gray-400" /></div>;
  return m.tipo === 'video'
    ? <video src={m.url} muted playsInline loop autoPlay className={`object-cover bg-black ${className}`} />
    : <img src={m.url} alt="" className={`object-cover bg-gray-100 ${className}`} />;
}

/** Vista previa aproximada de cómo se ve en cada red. */
function Vista({ red, cuenta, media, texto, tipo }) {
  const [i, setI] = useState(0);
  const R = RED[red];
  const m = media[Math.min(i, media.length - 1)];
  const largo = texto.length > 140;
  const [ver, setVer] = useState(false);
  useEffect(() => setI(0), [media.length]);
  const nombre = cuenta?.nombre || (red === 'instagram' ? '@tu.empresa' : 'Tu empresa');
  const cabecera = (
    <div className="flex items-center gap-2 px-3 py-2">
      {cuenta?.foto ? <img src={cuenta.foto} alt="" className="w-8 h-8 rounded-full object-cover" /> : <span className={`w-8 h-8 rounded-full flex items-center justify-center text-white ${R.fondo}`}><R.Icono size={14} /></span>}
      <div className="min-w-0"><div className="text-[13px] font-semibold text-gray-900 truncate">{nombre.replace(/^@/, red === 'instagram' ? '' : '@')}</div><div className="text-[11px] text-gray-500">{red === 'instagram' ? 'ahora' : 'Ahora · 🌎'}</div></div>
    </div>
  );
  const cuerpo = media.length > 0 && m && (
    <div className={`relative bg-black ${tipo === 'historia' || (tipo === 'reel' && red === 'instagram') ? 'aspect-[9/16] max-h-[420px] mx-auto' : red === 'instagram' ? 'aspect-square' : 'aspect-[4/3]'}`}>
      <Miniatura m={m} className="absolute inset-0 w-full h-full" />
      {media.length > 1 && (
        <>
          <button type="button" onClick={() => setI((i - 1 + media.length) % media.length)} className="absolute left-1.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/80 flex items-center justify-center" aria-label="Anterior"><ChevronLeft size={16} /></button>
          <button type="button" onClick={() => setI((i + 1) % media.length)} className="absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/80 flex items-center justify-center" aria-label="Siguiente"><ChevronRight size={16} /></button>
          <span className="absolute top-2 right-2 text-[11px] bg-black/60 text-white rounded-full px-2 py-0.5">{i + 1}/{media.length}</span>
        </>
      )}
    </div>
  );
  const textoV = (
    <div className="px-3 py-2 text-[13px] text-gray-800 whitespace-pre-wrap break-words">
      {red === 'instagram' && <b className="mr-1">{nombre.replace(/^@/, '')}</b>}
      {largo && !ver ? <>{texto.slice(0, 140)}… <button type="button" className="text-gray-500" onClick={() => setVer(true)}>más</button></> : texto || <span className="text-gray-400">(sin texto)</span>}
    </div>
  );
  if (red === 'instagram' && tipo === 'historia') {
    return <div className="rounded-xl overflow-hidden border border-gray-200 bg-black max-w-[260px] mx-auto">{cuerpo}<div className="text-[11px] text-white/80 text-center py-1.5">Historia · dura 24 h (el texto no se muestra)</div></div>;
  }
  return (
    <div className="rounded-xl overflow-hidden border border-gray-200 bg-white max-w-[380px] mx-auto">
      {cabecera}
      {red === 'instagram' ? <>{cuerpo}<div className="flex gap-3 px-3 pt-2 text-gray-800"><Heart size={20} /><MessageCircle size={20} /><Send size={20} /></div>{textoV}</> : <>{textoV}{cuerpo}<div className="flex justify-around border-t border-gray-100 py-2 text-xs text-gray-500"><span className="inline-flex items-center gap-1"><ThumbsUp size={14} />Me gusta</span><span className="inline-flex items-center gap-1"><MessageCircle size={14} />Comentar</span><span className="inline-flex items-center gap-1">{red === 'linkedin' ? <Repeat2 size={14} /> : <Send size={14} />}Compartir</span></div></>}
    </div>
  );
}

function motivoNo(red, tipo, media) {
  if (tipo === 'historia' && red !== 'instagram') return 'Las historias son sólo de Instagram';
  if (tipo === 'texto' && red === 'instagram') return 'Instagram necesita foto o video';
  if (red === 'instagram' && !media.length) return 'Elegí una foto o video';
  if (red !== 'instagram' && media.some((m) => m.tipo === 'video') && media.length > 1) return 'Un solo video por publicación';
  return null;
}

export default function Crear({ ctx, estado, inicial, onClose, onGuardada }) {
  // Mientras está abierta, el botón de ayuda (Licha) tapaba "Programar": se oculta y vuelve al cerrar.
  useEffect(() => {
    const host = document.getElementById('lisual-widget-host');
    if (!host) return undefined;
    const antes = host.style.display;
    host.style.display = 'none';
    return () => { host.style.display = antes; };
  }, []);
  const editando = inicial?.id ? inicial : null;
  const cuentas = useMemo(() => (estado?.redes || []).flatMap((r) => r.cuentas.map((c) => ({ ...c, red: r.red }))), [estado]);
  const [fuente, setFuente] = useState('fotos');
  const [fotos, setFotos] = useState(null);
  const [tl, setTl] = useState(undefined);
  const [media, setMedia] = useState(() => (editando ? editando.media.map((m) => ({ ...m })) : []));
  const [tipo, setTipo] = useState(() => (editando ? ({ historia: 'historia', texto: 'texto', reel: 'reel', video: 'reel' }[editando.tipo] || 'post') : inicial?.historia ? 'historia' : inicial?.reel ? 'reel' : 'post'));
  const [texto, setTexto] = useState(() => editando?.texto ?? inicial?.texto ?? '');
  const [hashtags, setHashtags] = useState(() => (editando?.hashtags || []).map((h) => '#' + h).join(' '));
  const [destinos, setDestinos] = useState(() => new Set(editando ? editando.destinos.map((d) => d.cuentaId) : cuentas.filter((c) => c.estado !== 'reconectar').map((c) => c.id)));
  const ini = editando ? partesAR(editando.fechaAt) : inicial?.fecha ? { fecha: inicial.fecha, hora: inicial.fecha === hoyAR() ? '18:00' : '09:00' } : { fecha: sumarDias(hoyAR(), 1), hora: '09:00' };
  const [modo, setModo] = useState('programar');
  const [fecha, setFecha] = useState(ini.fecha);
  const [hora, setHora] = useState(ini.hora);
  const [vista, setVista] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const fileRef = useRef(null);

  // Fotos de la obra
  useEffect(() => {
    let vivo = true;
    material(ctx.feedId).then((m) => vivo && setFotos(m.fotos)).catch(() => vivo && setFotos([]));
    return () => { vivo = false; };
  }, [ctx.feedId]);

  // Piezas que vienen de la vidriera: se suben al abrir
  useEffect(() => {
    if (!inicial?.archivos?.length) return;
    const lote = inicial.archivos.map((a, k) => ({ key: 'v' + k + Date.now(), url: '', tipo: a.tipo, origen: 'vidriera', subiendo: true }));
    setMedia(lote);
    inicial.archivos.forEach(async (a, k) => {
      try {
        const url = await subir(a.blob, ctx.customerId, a.tipo);
        setMedia((ms) => ms.map((m) => (m.key === lote[k].key ? { ...m, url, subiendo: false } : m)));
      } catch (e) {
        toast.error('No se pudo subir la pieza: ' + errorMsg(e));
        setMedia((ms) => ms.filter((m) => m.key !== lote[k].key));
      }
    });
  }, [inicial, ctx.customerId]);

  useEffect(() => {
    const esc = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', esc);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', esc); document.body.style.overflow = ''; };
  }, [onClose]);

  const abrirTimelapse = async () => {
    setFuente('timelapse');
    if (tl === undefined) { setTl(null); setTl((await ultimoTimelapse(ctx.feedId)) || false); }
  };

  const esta = (url) => media.some((m) => m.url === url);
  const alternar = (item) => {
    if (esta(item.url)) return setMedia(media.filter((m) => m.url !== item.url));
    if (item.tipo === 'video') { setMedia([item]); if (tipo === 'post' || tipo === 'texto') setTipo('reel'); return; }
    const sinVideos = media.filter((m) => m.tipo !== 'video');
    if (tipo === 'historia') return setMedia([item]);
    if (sinVideos.length >= 10) return toast.info('Hasta 10 fotos por publicación');
    setMedia([...sinVideos, item]);
    if (tipo === 'reel' || tipo === 'texto') setTipo('post');
  };
  const antesDespues = () => {
    if (!fotos || fotos.length < 2) return toast.info('Todavía no hay fotos suficientes');
    setMedia([{ ...fotos[0], tipo: 'foto', origen: 'antes_despues' }, { ...fotos[fotos.length - 1], tipo: 'foto', origen: 'antes_despues' }]);
    setTipo('post');
    if (!texto) setTexto(completar(PLANTILLAS.find((p) => p.id === 'antes').texto, ctx));
  };
  const subirPc = async (files) => {
    for (const f of Array.from(files || []).slice(0, 10)) {
      const tipoM = f.type.startsWith('video') ? 'video' : 'foto';
      if (tipoM === 'video' && !/mp4|quicktime/.test(f.type)) { toast.error('El video tiene que ser MP4 o MOV'); continue; }
      const key = 'p' + Math.random();
      setMedia((ms) => (tipoM === 'video' ? [] : ms.filter((m) => m.tipo !== 'video')).concat({ key, url: '', tipo: tipoM, origen: 'subida', subiendo: true }));
      if (tipoM === 'video') setTipo('reel');
      try {
        const url = await subir(f, ctx.customerId, tipoM);
        setMedia((ms) => ms.map((m) => (m.key === key ? { ...m, url, subiendo: false } : m)));
      } catch (e) {
        toast.error(`No se pudo subir ${f.name}`);
        setMedia((ms) => ms.filter((m) => m.key !== key));
      }
    }
  };
  const mover = (k, d) => { const a = media.slice(); const j = k + d; if (j < 0 || j >= a.length) return; [a[k], a[j]] = [a[j], a[k]]; setMedia(a); };

  const listos = media.filter((m) => !m.subiendo && m.url);
  const tipoB = tipoBackend(tipo, listos);
  const elegidas = cuentas.filter((c) => destinos.has(c.id));
  const bloqueadas = elegidas.filter((c) => motivoNo(c.red, tipo, listos));
  const redVista = vista && elegidas.some((c) => c.red === vista) ? vista : elegidas[0]?.red || 'instagram';
  const hashList = hashtags.split(/[\s,]+/).map((h) => h.replace(/^#+/, '')).filter(Boolean);
  const largoTotal = texto.length + hashList.reduce((a, h) => a + h.length + 2, 0);
  const tope = Math.min(...(elegidas.length ? elegidas.map((c) => estado?.limites?.texto?.[c.red] || 2200) : [2200]));
  const textoVista = [texto, hashList.filter((h) => !new RegExp('#' + h + '\\b', 'i').test(texto)).map((h) => '#' + h).join(' ')].filter(Boolean).join('\n\n');

  const atajos = (() => {
    const hoy = hoyAR();
    const ahoraH = partesAR(new Date().toISOString()).hora;
    const dow = new Date(`${hoy}T12:00:00-03:00`).getDay();
    const sab = sumarDias(hoy, ((6 - dow + 7) % 7) || 7);
    return [
      ...(ahoraH < '17:30' ? [['Hoy 18:00', hoy, '18:00']] : []),
      ['Mañana 9:00', sumarDias(hoy, 1), '09:00'],
      ['Mañana 13:00', sumarDias(hoy, 1), '13:00'],
      ['Sábado 10:00', sab, '10:00'],
    ];
  })();

  const guardar = async () => {
    if (media.some((m) => m.subiendo)) return toast.info('Esperá a que terminen de subir los archivos');
    if (!elegidas.length) return toast.error('Elegí al menos una red');
    if (bloqueadas.length) return toast.error(`${bloqueadas[0].nombre}: ${motivoNo(bloqueadas[0].red, tipo, listos)}`);
    const body = {
      customerId: ctx.customerId,
      feedId: ctx.feedId,
      obra: ctx.obra,
      destinos: elegidas.map((c) => c.id),
      tipo: tipoB,
      texto,
      hashtags: hashList,
      media: tipo === 'texto' ? [] : listos.map(({ url, tipo: t, origen }) => ({ url, tipo: t, origen })),
      ...(modo === 'ahora' ? { ahora: true } : { fechaAt: isoAR(fecha, hora) }),
    };
    setEnviando(true);
    try {
      const p = editando ? await redes.editar(editando.id, body) : await redes.crear(body);
      toast.success(modo === 'ahora' ? 'Publicando… en unos segundos lo ves en el calendario' : `Programada para el ${cuando(p.fechaAt)}`);
      onGuardada(p);
    } catch (e) {
      toast.error(errorMsg(e));
    } finally {
      setEnviando(false);
    }
  };

  const tab = (id, label, Icon, fn) => (
    <button key={id} type="button" onClick={fn || (() => setFuente(id))} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap ${fuente === id ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`}><Icon size={15} />{label}</button>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-stretch md:items-center justify-center md:p-4" onClick={(e) => e.target === e.currentTarget && onClose()} role="dialog" aria-modal="true" aria-label="Crear publicación">
      <div className="bg-gray-50 w-full md:max-w-6xl md:rounded-2xl shadow-2xl flex flex-col max-h-full md:max-h-[94vh] overflow-hidden">
        <div className="flex items-center justify-between px-4 md:px-6 py-3 bg-white border-b border-gray-200">
          <div>
            <h3 className="text-lg font-bold text-gray-900">{editando ? 'Editar publicación' : 'Crear publicación'}</h3>
            <p className="text-xs text-gray-500">{ctx.obra}{estado?.modo === 'simulado' ? ' · modo prueba: no sale a ninguna red real' : ''}</p>
          </div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50" aria-label="Cerrar"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_400px] gap-4 p-4 md:p-6">
            <div className="space-y-4 min-w-0">
              {/* 1. Contenido */}
              <section className="bg-white border border-gray-200 rounded-2xl p-4 space-y-3">
                <div className="text-sm font-semibold text-gray-900">1. ¿Qué querés publicar?</div>
                <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                  {TIPOS.filter((t) => !t.redes || t.redes.some((r) => (estado?.redes || []).some((x) => x.red === r)) || t.id === tipo).map((t) => (
                    <button key={t.id} type="button" onClick={() => { setTipo(t.id); if (t.id === 'historia') setMedia(media.slice(0, 1)); if (t.id === 'reel') setMedia(media.filter((m) => m.tipo === 'video').slice(0, 1)); }} className={`text-left px-3 py-2 rounded-xl border text-sm ${tipo === t.id ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`}>
                      <div className="font-semibold">{t.t}</div><div className="text-[11px] opacity-80">{t.d}</div>
                    </button>
                  ))}
                </div>
                {tipo !== 'texto' && (
                  <>
                    <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                      {tab('fotos', 'Fotos de la obra', ImageIcon)}
                      {tab('timelapse', 'Timelapse', Film, abrirTimelapse)}
                      {tab('antes', 'Antes y después', Columns2, () => { setFuente('antes'); })}
                      {tab('vidriera', 'Plantillas', Sparkles)}
                      {tab('pc', 'Subir de la PC', Upload, () => { setFuente('pc'); })}
                    </div>
                    {fuente === 'fotos' && (
                      !fotos ? <div className="h-28 flex items-center justify-center"><Loader2 className="animate-spin text-gray-400" /></div>
                        : !fotos.length ? <p className="text-sm text-gray-500">Todavía no encontramos fotos de esta cámara.</p>
                          : <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-72 overflow-y-auto pr-1">
                            {fotos.slice().reverse().map((f) => (
                              <button key={f.url} type="button" onClick={() => alternar({ url: f.url, tipo: 'foto', origen: 'foto_obra' })} className={`relative rounded-lg overflow-hidden border-2 ${esta(f.url) ? 'border-blue-600' : 'border-transparent'}`}>
                                <img src={f.url} alt="" loading="lazy" className="w-full aspect-square object-cover" />
                                <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent text-white text-[10px] px-1 py-0.5 text-left">{f.etiqueta}</span>
                                {esta(f.url) && <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">{media.findIndex((m) => m.url === f.url) + 1}</span>}
                              </button>
                            ))}
                          </div>
                    )}
                    {fuente === 'timelapse' && (
                      tl === null ? <div className="h-28 flex items-center justify-center"><Loader2 className="animate-spin text-gray-400" /></div>
                        : tl && tl.url ? (
                          <button type="button" onClick={() => alternar({ url: tl.url, tipo: 'video', origen: 'timelapse' })} className={`relative w-full sm:w-72 rounded-xl overflow-hidden border-2 ${esta(tl.url) ? 'border-blue-600' : 'border-gray-200'}`}>
                            <video src={tl.url} muted playsInline loop autoPlay className="w-full aspect-video object-cover bg-black" />
                            <span className="absolute bottom-2 left-2 text-xs font-semibold bg-black/60 text-white rounded px-2 py-0.5">{esta(tl.url) ? '✓ Elegido' : 'Tocá para usarlo'}</span>
                          </button>
                        ) : <p className="text-sm text-gray-500">{tl?.pendiente ? 'El timelapse se está armando. Abrí Obra › Timelapse y volvé en unos minutos.' : 'No encontramos un timelapse listo de esta cámara.'}</p>
                    )}
                    {fuente === 'antes' && (
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <p className="text-sm text-gray-600 flex-1">Arma un carrusel con la primera foto de la obra y la de hoy: el clásico "antes y después" que más se comparte.</p>
                        <button type="button" onClick={antesDespues} className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white"><Columns2 size={16} />Usar antes y después</button>
                      </div>
                    )}
                    {fuente === 'vidriera' && <p className="text-sm text-gray-600">Cerrá esta ventana, abrí la pestaña <b>Plantillas</b>, elegí una pieza y tocá <b>"Programar en mis redes"</b>: vuelve acá con la pieza lista.</p>}
                    {fuente === 'pc' && (
                      <div>
                        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime" multiple className="hidden" onChange={(e) => { subirPc(e.target.files); e.target.value = ''; }} />
                        <button type="button" onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-gray-300 rounded-xl py-6 text-sm text-gray-600 hover:bg-gray-50 inline-flex flex-col items-center gap-1"><Upload size={20} />Elegí fotos (JPG/PNG) o un video MP4</button>
                      </div>
                    )}
                    {media.length > 0 && (
                      <div>
                        <div className="text-xs font-medium text-gray-600 mb-1.5">Elegidos ({media.length}) · el primero es la portada</div>
                        <div className="flex gap-2 overflow-x-auto pb-1">
                          {media.map((m, k) => (
                            <div key={m.key || m.url} className="relative shrink-0">
                              <Miniatura m={m} className="w-20 h-20 rounded-lg" />
                              <button type="button" onClick={() => setMedia(media.filter((_, j) => j !== k))} className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-white border border-gray-200 shadow flex items-center justify-center text-gray-600 hover:text-red-600" aria-label="Quitar"><Trash2 size={12} /></button>
                              {media.length > 1 && <div className="absolute bottom-1 inset-x-1 flex justify-between"><button type="button" onClick={() => mover(k, -1)} className="w-5 h-5 rounded bg-white/90 flex items-center justify-center" aria-label="Mover antes"><ChevronLeft size={12} /></button><button type="button" onClick={() => mover(k, 1)} className="w-5 h-5 rounded bg-white/90 flex items-center justify-center" aria-label="Mover después"><ChevronRight size={12} /></button></div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </section>

              {/* 2. Texto */}
              <section className="bg-white border border-gray-200 rounded-2xl p-4 space-y-3">
                <div className="text-sm font-semibold text-gray-900">2. Texto</div>
                <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                  {PLANTILLAS.map((p) => <button key={p.id} type="button" onClick={() => setTexto(completar(p.texto, ctx))} className="text-xs font-medium px-3 py-1.5 rounded-full border border-gray-200 bg-white hover:bg-gray-50 whitespace-nowrap">{p.t}</button>)}
                </div>
                <textarea aria-label="Texto de la publicación" rows={6} className={`${input} resize-y`} style={{ minHeight: 140, lineHeight: 1.55 }} value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Escribí lo que quieras contar o tocá una sugerencia de arriba" />
                <div className={`text-[11px] text-right -mt-2 ${largoTotal > tope ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>{largoTotal}/{tope}</div>
                <label className="text-xs font-medium text-gray-600" htmlFor="redes-hash">Hashtags</label>
                <input id="redes-hash" className={input} value={hashtags} onChange={(e) => setHashtags(e.target.value)} placeholder="#obra #construccion" />
                <div className="flex flex-wrap gap-1.5">
                  {HASHTAGS.filter((h) => !hashList.map((x) => x.toLowerCase()).includes(h)).slice(0, 8).map((h) => <button key={h} type="button" onClick={() => setHashtags((hashtags + ' #' + h).trim())} className="text-[11px] px-2 py-1 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200">#{h}</button>)}
                </div>
              </section>

              {/* 3. Dónde y cuándo */}
              <section className="bg-white border border-gray-200 rounded-2xl p-4 space-y-3">
                <div className="text-sm font-semibold text-gray-900">3. Dónde y cuándo</div>
                {!cuentas.length ? <p className="text-sm text-gray-500">Primero conectá al menos una red (arriba de todo, en "Tus redes").</p> : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {cuentas.map((c) => {
                      const R = RED[c.red];
                      const no = motivoNo(c.red, tipo, listos);
                      const on = destinos.has(c.id);
                      return (
                        <label key={c.id} className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 cursor-pointer ${on ? 'border-blue-600 bg-blue-50' : 'border-gray-200'} ${c.estado === 'reconectar' ? 'opacity-50 cursor-not-allowed' : ''}`}>
                          <input type="checkbox" className="sr-only" checked={on} disabled={c.estado === 'reconectar'} onChange={() => { const s = new Set(destinos); on ? s.delete(c.id) : s.add(c.id); setDestinos(s); }} />
                          <span className={`w-5 h-5 rounded-md border flex items-center justify-center ${on ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 bg-white'}`}>{on && <Check size={13} />}</span>
                          <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-white shrink-0 ${R.fondo}`}><R.Icono size={14} /></span>
                          <span className="min-w-0"><span className="block text-sm font-semibold text-gray-800 truncate">{c.nombre}</span>{on && no ? <span className="block text-[11px] text-red-600">{no}</span> : c.estado === 'reconectar' ? <span className="block text-[11px] text-red-600">Volvé a conectarla</span> : <span className="block text-[11px] text-gray-500">{R.nombre}</span>}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                <div className="flex gap-2">
                  {[['programar', 'Programar', CalendarClock], ['ahora', 'Publicar ahora', Send]].map(([id, t, I]) => (
                    <button key={id} type="button" onClick={() => setModo(id)} className={`flex-1 inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold border ${modo === id ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-gray-200 text-gray-700'}`}><I size={15} />{t}</button>
                  ))}
                </div>
                {modo === 'programar' && (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <div><label className="text-xs font-medium text-gray-600" htmlFor="redes-fecha">Día</label><input id="redes-fecha" type="date" className={input} min={hoyAR()} value={fecha} onChange={(e) => setFecha(e.target.value)} /></div>
                      <div><label className="text-xs font-medium text-gray-600" htmlFor="redes-hora">Hora (Argentina)</label><input id="redes-hora" type="time" className={input} step={300} value={hora} onChange={(e) => setHora(e.target.value)} /></div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {atajos.map(([t, f, h]) => <button key={t} type="button" onClick={() => { setFecha(f); setHora(h); }} className={`text-xs px-2.5 py-1 rounded-full border ${fecha === f && hora === h ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}>{t}</button>)}
                    </div>
                  </>
                )}
              </section>
            </div>

            {/* Vista previa */}
            <aside className="space-y-3 lg:sticky lg:top-0 self-start">
              <div className="bg-white border border-gray-200 rounded-2xl p-4 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-gray-900">Vista previa</div>
                  <div className="flex gap-1">
                    {['instagram', 'facebook', 'linkedin'].filter((r) => !elegidas.length || elegidas.some((c) => c.red === r)).map((r) => {
                      const I = RED[r].Icono;
                      return <button key={r} type="button" onClick={() => setVista(r)} title={RED[r].nombre} aria-label={`Ver en ${RED[r].nombre}`} className={`w-8 h-8 rounded-lg flex items-center justify-center ${redVista === r ? `text-white ${RED[r].fondo}` : 'bg-gray-100 text-gray-600'}`}><I size={15} /></button>;
                    })}
                  </div>
                </div>
                <Vista red={redVista} cuenta={elegidas.find((c) => c.red === redVista)} media={tipo === 'texto' ? [] : media} texto={textoVista} tipo={tipo} />
                {bloqueadas.length > 0 && <div className="text-xs bg-amber-50 text-amber-800 rounded-lg px-3 py-2 flex gap-2"><AlertTriangle size={14} className="shrink-0 mt-0.5" />{bloqueadas.map((c) => `${c.nombre}: ${motivoNo(c.red, tipo, listos)}`).join(' · ')}</div>}
              </div>
            </aside>
          </div>
        </div>

        <div className="bg-white border-t border-gray-200 px-4 md:px-6 py-3 flex items-center justify-between gap-3">
          <div className="text-xs text-gray-500 hidden sm:block">{modo === 'programar' ? `Sale el ${cuando(isoAR(fecha, hora))} en ${elegidas.length} red${elegidas.length === 1 ? '' : 'es'}` : `Sale apenas confirmes, en ${elegidas.length} red${elegidas.length === 1 ? '' : 'es'}`}</div>
          <div className="flex gap-2 ml-auto w-full sm:w-auto">
            <button type="button" onClick={onClose} className="flex-1 sm:flex-none rounded-lg px-4 py-2.5 text-sm font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50">Cancelar</button>
            <button type="button" onClick={guardar} disabled={enviando} className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60">
              {enviando ? <Loader2 size={16} className="animate-spin" /> : modo === 'ahora' ? <Send size={16} /> : <CalendarClock size={16} />}
              {editando ? 'Guardar cambios' : modo === 'ahora' ? 'Publicar ahora' : 'Programar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
